// src/pages/TeacherDashboard.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFunctions, httpsCallable } from "firebase/functions";

import {
  doc,
  getDoc,
  collection,
  collectionGroup,
  query,
  where,
  onSnapshot,
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged, signOut, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { Bar, Line, Pie } from "react-chartjs-2";
import { Tabs, Tab, Modal, Box, MenuItem, Select, InputLabel, FormControl } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { app, db } from "../firebase";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  ChartTitle,
  Tooltip,
  Legend
);

/* ---------- robust date utils (fixes N/A renew & wrong periods) ---------- */
function msFromAny(v) {
  if (!v) return null;
  if (typeof v === "number") return v > 1e12 ? v : v * 1000; // seconds → ms
  if (v?.toMillis) { try { return v.toMillis(); } catch {} } // Firestore Timestamp
  if (v?._seconds != null) return v._seconds * 1000;         // admin SDK shape
  if (typeof v?.seconds === "number") return v.seconds * 1000;
  const d = new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}
function addMonths(date, n) { const d = new Date(date); d.setMonth(d.getMonth() + n); return d; }
function currentPeriodFromAnchor(anchorMs, now = new Date()) {
  if (!anchorMs) return null;
  const anchor = new Date(anchorMs);
  if (Number.isNaN(anchor.getTime())) return null;
  let start = new Date(anchor);
  let end = addMonths(start, 1);
  while (now >= end) { start = end; end = addMonths(start, 1); }
  return { start, end }; // end is exclusive
}
function formatDate(d) {
  if (!d) return "N/A";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/* ---------- small utils ---------- */
function timeAgo(ts) {
  if (!ts) return "just now";
  const ms = typeof ts === "number" ? ts : (ts?.seconds ? ts.seconds * 1000 : (msFromAny(ts) ?? Date.now()));
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const isMobile = useMediaQuery("(max-width: 768px)");

  /* ----------------------------- identity ----------------------------- */
  const [uid, setUid] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  /* ------------------------- teacher + school ------------------------- */
  const [teacherDoc, setTeacherDoc] = useState(null);

  /* ----------------------- students + themes, etc --------------------- */
  const [students, setStudents] = useState([]); // from /students
  const [themes, setThemes] = useState([]);
  const [recordings, setRecordings] = useState([]);

  /* ---------------------- viewed receipts (persist) ------------------- */
  const [viewedMap, setViewedMap] = useState({}); // { recKey: true }

  /* ------------------------- billing / period ctx --------------------- */
  const [schoolCtx, setSchoolCtx] = useState(null); // { periodStart, periodEnd, perStudentCap, billingAnchorMs }

  /* -------------------------------- UI -------------------------------- */
  const [tab, setTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [insightModalOpen, setInsightModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Theme modal state (used for create & edit)
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState("");
  const [themeSetting, setThemeSetting] = useState("");
  const [themeDescription, setThemeDescription] = useState("");
  const [themeComment, setThemeComment] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [expiryDate, setExpiryDate] = useState("");
  const [editingThemeRef, setEditingThemeRef] = useState(null);  // holds DocumentReference when editing
  const [editingThemeDoc, setEditingThemeDoc] = useState(null);  // original theme doc for locked fields

  // Quick filter by class (within the current teacher's class data set)
  const [filterClass, setFilterClass] = useState("");


  const [pwOpen, setPwOpen] = useState(false);
const [pwCurrent, setPwCurrent] = useState("");
const [pwNew, setPwNew] = useState("");
const [pwConfirm, setPwConfirm] = useState("");
const [pwSaving, setPwSaving] = useState(false);


  /* --------------------------- auth bootstrap -------------------------- */
  useEffect(() => {
    const off = onAuthStateChanged(auth, (u) => setUid(u?.uid || null));
    return () => off();
  }, [auth]);

  /* ------------------------ users/{uid} live read ---------------------- */
  useEffect(() => {
    if (!uid) return;
    const uref = doc(db, "users", uid);
    const unsub = onSnapshot(
      uref,
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setUserDoc(data);
        setUserLoading(false);
      },
      () => setUserLoading(false)
    );
    return () => unsub();
  }, [uid]);

  /* ---------------------- teachers/{uid} live read --------------------- */
  useEffect(() => {
    if (!uid) return;
    const tref = doc(db, "teachers", uid);
    const unsub = onSnapshot(
      tref,
      (snap) => setTeacherDoc(snap.exists() ? snap.data() : null),
      () => setTeacherDoc(null)
    );
    return () => unsub();
  }, [uid]);

  // Resolve schoolId robustly: users → teachers → localStorage
  const schoolId = useMemo(() => {
    return (
      userDoc?.orgId ||
      userDoc?.schoolId ||
      teacherDoc?.schoolId ||
      localStorage.getItem("schoolId") ||
      null
    );
  }, [userDoc, teacherDoc]);

  const className =
    teacherDoc?.className || localStorage.getItem("className") || null;

  // School name (cached)
  useEffect(() => {
    const nameFromTeacher = teacherDoc?.schoolName;
    const nameFromUser = userDoc?.schoolName;
    const cached = localStorage.getItem("schoolName");
    const best =
      (nameFromTeacher && String(nameFromTeacher)) ||
      (nameFromUser && String(nameFromUser)) ||
      (cached && String(cached)) ||
      "";
    if (best && cached !== best) {
      localStorage.setItem("schoolName", best);
    }
  }, [teacherDoc?.schoolName, userDoc?.schoolName]);

  const schoolName =
    (teacherDoc?.schoolName && String(teacherDoc.schoolName)) ||
    (userDoc?.schoolName && String(userDoc.schoolName)) ||
    String(localStorage.getItem("schoolName") || "") ||
    "School";

  /* ------------------- Read students for this class -------------------- */
  useEffect(() => {
    if (!schoolId || !className) {
      setStudents([]);
      return;
    }
    const qRef = query(
      collection(db, "students"),
      where("schoolId", "==", schoolId),
      where("className", "==", className)
    );
    const unsub = onSnapshot(
      qRef,
      (qs) => setStudents(qs.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => {
        console.warn("students onSnapshot:", e?.code || e?.message || e);
        setStudents([]);
      }
    );
    return () => unsub();
  }, [schoolId, className]);

  /* ----------------------- Read themes for class (CG) ------------------ */
  useEffect(() => {
    let unsubs = [];
    setThemes([]);

    if (!schoolId || !className) return;

    const q1 = query(
      collectionGroup(db, "themes"),
      where("schoolId", "==", schoolId),
      where("classes", "array-contains", className),
      orderBy("createdAt", "desc")
    );

    const q2 = query(
      collectionGroup(db, "themes"),
      where("schoolId", "==", schoolId),
      where("className", "==", className),
      orderBy("createdAt", "desc")
    );

    const toMs = (v) => msFromAny(v) ?? 0;

    let bag1 = [], bag2 = [];
    const mergeAndSet = () => {
      const map = new Map();
      [...bag1, ...bag2].forEach((d) => {
        const key = `${d.__path || ""}#${d.id}`;
        if (!map.has(key)) map.set(key, d);
      });
      const arr = Array.from(map.values());
      arr.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
      setThemes(arr);
    };

    const unsub1 = onSnapshot(
      q1,
      (qs) => {
        bag1 = qs.docs.map((d) => ({ id: d.id, __path: d.ref.path, ref: d.ref, ...d.data() })); // keep ref
        mergeAndSet();
      },
      (e) => console.warn("[themes:q1] onSnapshot error:", e?.code || e?.message || e)
    );
    unsubs.push(unsub1);

    const unsub2 = onSnapshot(
      q2,
      (qs) => {
        bag2 = qs.docs.map((d) => ({ id: d.id, __path: d.ref.path, ref: d.ref, ...d.data() })); // keep ref
        mergeAndSet();
      },
      (e) => console.warn("[themes:q2] onSnapshot error:", e?.code || e?.message || e)
    );
    unsubs.push(unsub2);

    return () => { unsubs.forEach((u) => { try { u(); } catch {} }); };
  }, [schoolId, className]);

  /* ------------- Load teacher's viewed receipts for NEW badges --------- */
  useEffect(() => {
    if (!uid) return;
    const vRef = collection(db, "teachers", uid, "viewedRecordings");
    const unsub = onSnapshot(
      vRef,
      (qs) => {
        const map = {};
        qs.forEach((d) => { map[d.id] = true; });
        setViewedMap(map);
      },
      (e) => {
        console.warn("viewedRecordings onSnapshot:", e?.code || e?.message || e);
        setViewedMap({});
      }
    );
    return () => unsub();
  }, [uid]);


  /* -------------------------- Load billing context --------------------- */
  useEffect(() => {
    let cancelled = false;

    async function fetchCtx() {
      try {
        if (!schoolId) {
          setSchoolCtx(null);
          return;
        }

        const fns = getFunctions(app, "us-central1");
        const getSchoolContext = httpsCallable(fns, "getSchoolContext");
        const { data } = await getSchoolContext({ orgId: schoolId });

        if (cancelled) return;

        setSchoolCtx({
          periodStart: data?.periodStart ? new Date(msFromAny(data.periodStart)) : null,
          periodEnd: data?.periodEnd ? new Date(msFromAny(data.periodEnd)) : null,
          perStudentCap: Number(data?.perStudentCap || 40),
          billingAnchorMs:
            msFromAny(data?.billingAnchorMs) ??
            msFromAny(data?.orgCreatedAt) ??
            null,
          displayName: data?.displayName || null,
        });
      } catch (e) {
        console.warn("getSchoolContext:", e?.message || e);
        if (!cancelled) setSchoolCtx(null);
      }
    }

    fetchCtx();

    return () => {
      cancelled = true;
    };
  }, [schoolId]);
 

  /* ---------------------- Build recordings view ------------------------ */
  useEffect(() => {
    const list = [];
    students.forEach((s) => {
      const name = `${s.firstName || ""} ${s.lastName || ""}`.trim() || "Student";
      (s.recordings || []).forEach((rec) => {
        if (rec?.sentToTeacher) {
          const recKey = `${s.id}__${rec.fileName || rec.id}`;
          list.push({
            ...rec,
            recKey,
            viewedByTeacher: !!viewedMap[recKey],
            studentName: name,
            studentId: s.id,
            uploadedAt: rec?.uploadedAt || rec?.createdAt || null,
            className: s.className,
            schoolId: s.schoolId,
          });
        }
      });
    });
    list.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));
    setRecordings(list);
  }, [students, viewedMap]);

  /* ----------------------- computed chart numbers ---------------------- */
  const totalStories = useMemo(
    () => students.reduce((sum, s) => sum + (Number(s.storiesRead) || 0), 0),
    [students]
  );

  const avgReadingTime = useMemo(() => {
    if (!students.length) return 0;
    const totalSeconds = students.reduce(
      (sum, s) => sum + (Number(s.totalReadingTime) || 0),
      0
    );
    return Math.round(totalSeconds / students.length / 60);
  }, [students]);

  const topStudent = useMemo(() => {
    return students.reduce(
      (top, s) =>
        (Number(s.storiesRead) || 0) > (Number(top.storiesRead) || 0) ? s : top,
      {}
    );
  }, [students]);

  // CLASS FILTER options (within teacher's class data; often one option)
  const classOptions = useMemo(
    () => {
      const set = new Set(students.map((s) => s.className || ""));
      return Array.from(set).filter(Boolean);
    },
    [students]
  );

  // Base filter by search
  const filteredBySearch = useMemo(
    () =>
      students.filter((s) =>
        `${s.firstName || ""} ${s.lastName || ""}`.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [students, searchTerm]
  );

  // Apply class filter
  const displayedStudents = useMemo(() => {
    if (!filterClass) return filteredBySearch;
    return filteredBySearch.filter((s) => (s.className || "") === filterClass);
  }, [filteredBySearch, filterClass]);

  // Chart datasets (use displayedStudents)
  const storiesData = useMemo(
    () => ({
      labels: displayedStudents.map((s) => `${s.firstName || ""} ${s.lastName || ""}`.trim()),
      datasets: [
        {
          label: "Stories Read",
          data: displayedStudents.map((s) => Number(s.storiesRead) || 0),
          backgroundColor: "rgba(54, 162, 235, 0.6)",
        },
      ],
    }),
    [displayedStudents]
  );

  const readingTimeData = useMemo(
    () => ({
      labels: displayedStudents.map((s) => `${s.firstName || ""} ${s.lastName || ""}`.trim()),
      datasets: [
        {
          label: "Reading Time (mins)",
          data: displayedStudents.map((s) => Math.round((Number(s.totalReadingTime) || 0) / 60)),
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
        },
      ],
    }),
    [displayedStudents]
  );

  const genresData = useMemo(() => {
    const genreMap = {};
    displayedStudents.forEach((s) => {
      const g = s.genresExplored || s.favoriteGenres || {};
      Object.entries(g).forEach(([genre, count]) => {
        genreMap[genre] = (genreMap[genre] || 0) + (Number(count) || 0);
      });
    });
    return {
      labels: Object.keys(genreMap),
      datasets: [
        {
          label: "Genre Mentions",
          data: Object.values(genreMap),
          backgroundColor: "rgba(255,193,7,0.5)",
          borderColor: "rgba(255,193,7,1)",
          borderWidth: 1,
        },
      ],
    };
  }, [displayedStudents]);

  const engagementData = useMemo(
    () => ({
      labels: displayedStudents.map((s) => `${s.firstName || ""} ${s.lastName || ""}`.trim()),
      datasets: [
        {
          label: "Active Days",
          data: displayedStudents.map((s) => (Array.isArray(s.readingActivity) ? s.readingActivity.length : 0)),
          backgroundColor: "rgba(153,102,255,0.5)",
          borderColor: "rgba(153,102,255,1)",
          borderWidth: 1,
        },
      ],
    }),
    [displayedStudents]
  );

const chartCardStyle = {
  background: "white",
  padding: "20px",
  borderRadius: "10px",
  minHeight: "260px",
  display: "flex",
  flexDirection: "column",
};


  // Unread badge count
  const unreadCount = useMemo(
    () => recordings.filter((r) => !r.viewedByTeacher).length,
    [recordings]
  );

  // Leaderboard (top 5 by stories; includes avg time)
  const leaderboard = useMemo(() => {
    const rows = displayedStudents.map((s) => {
      const stories = Number(s.storiesRead) || 0;
      const totalMin = Math.round((Number(s.totalReadingTime) || 0) / 60);
      const avgMin = stories ? Math.round(totalMin / stories) : 0;
      return {
        id: s.id,
        name: `${s.firstName || ""} ${s.lastName || ""}`.trim() || "Student",
        stories,
        avgMin,
      };
    });
    rows.sort((a, b) => b.stories - a.stories || a.avgMin - b.avgMin);
    return rows.slice(0, 5).map((r, i) => ({ ...r, rank: i + 1 }));
  }, [displayedStudents]);

  // Recent 3 submissions (recordings)
  const recentSubmissions = useMemo(() => {
    return recordings.slice(0, 3).map((r) => ({
      who: r.studentName || "Student",
      what: r.storyTitle || r.fileName || "Recording",
      when: r.uploadedAt || r.createdAt || Date.now(),
    }));
  }, [recordings]);


/* -------- user-level fallback for billing window (mirrored onto users/{uid}) -------- */
const userWindow = useMemo(() => {
  const sub = userDoc?.subscription || null;
  if (!sub) return null;

  const startMs = msFromAny(sub.currentPeriodStart);
  const endMs   = msFromAny(sub.currentPeriodEnd);
  const displayName = sub.displayName || null;
  const billingInterval = sub.billingInterval || null;

  if (startMs && endMs) {
    return {
      start: new Date(startMs),
      end: new Date(endMs),           // end is exclusive if you wrote it that way
      displayName,
      billingInterval,
      source: "userDoc",
    };
  }
  return null;
}, [userDoc]);


  /* --------------------------- billing window UI ----------------------- */
  /* --------------------------- pick billing window --------------------------- */
const billingWindow = useMemo(() => {
  // 1) Prefer explicit billing anchor from school context
  const schoolAnchorMs =
    schoolCtx?.billingAnchorMs ??
    msFromAny(schoolCtx?.periodStart) ??
    null;

  const viaSchoolAnchor = currentPeriodFromAnchor(schoolAnchorMs);
  if (viaSchoolAnchor) {
    return {
      start: viaSchoolAnchor.start,
      end: viaSchoolAnchor.end,
      label: schoolCtx?.displayName || null,
      source: "schoolCtx.anchor",
    };
  }

  // 2) Fallback to mirrored user subscription dates
  if (userWindow?.start && userWindow?.end) {
    return {
      start: userWindow.start,
      end: userWindow.end,
      label: userWindow.displayName || null,
      source: "userDoc",
    };
  }

  // 3) Last resort: calendar month
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(now.getFullYear(), now.getMonth() + 1, 1), // exclusive
    label: null,
    source: "calendar-month",
  };
}, [schoolCtx, userWindow]);


  const periodStart = billingWindow?.start || null;
  const periodEndExclusive = billingWindow?.end || null;
  const periodEndDisplay = periodEndExclusive
    ? new Date(periodEndExclusive.getTime() - 24 * 60 * 60 * 1000)
    : null;
  const renewDate = periodEndExclusive || null;

  /* ---------------------------- UI handlers ---------------------------- */
  const handleLogout = async () => {
    try { await signOut(auth); } finally { navigate("/"); }
  };

    const handleGenerateStory = () => {
    const teacherProfile =
      `${teacherDoc?.firstName || ""} ${teacherDoc?.lastName || ""}`.trim() ||
      userDoc?.fullName ||
      auth.currentUser?.email ||
      "Teacher";

    navigate(
  `/storyapp?profile=${encodeURIComponent(teacherProfile)}`,
  {
    state: {
      fromDashboard: "teacher",
      returnTo: "/teacher-dashboard",
      userRole: "teacher",
    },
  }
);
  };

  const handleOpenModal = (student) => {
    setSelectedStudent(student);
    setInsightModalOpen(true);
  };

  // mark recording viewed
  const markRecViewed = useCallback(
    async (r) => {
      if (!uid || !r?.recKey) return;
      try {
        await setDoc(
          doc(db, "teachers", uid, "viewedRecordings", r.recKey),
          { viewedAt: serverTimestamp(), studentId: r.studentId || null, fileName: r.fileName || r.id || null },
          { merge: true }
        );
        setViewedMap((prev) => ({ ...prev, [r.recKey]: true }));
      } catch (e) {
        console.warn("markRecViewed:", e?.message || e);
      }
    },
    [uid]
  );

  const deleteRecordingFromTeacher = useCallback(
    async (r) => {
      try {
        if (!r?.studentId) return;
        const sref = doc(db, "students", r.studentId);
        const snap = await getDoc(sref);
        if (!snap.exists()) return;

        const data = snap.data() || {};
        const updated = (Array.isArray(data.recordings) ? data.recordings : []).map(
          (rec) =>
            (rec.fileName || rec.id) === (r.fileName || r.id)
              ? { ...rec, sentToTeacher: false, viewedByTeacher: false }
              : rec
        );
        await updateDoc(sref, { recordings: updated });
        setRecordings((prev) => prev.filter((x) => (x.fileName || x.id) !== (r.fileName || r.id)));
        toast.success("Recording removed from your dashboard.");
      } catch (e) {
        console.warn("deleteRecordingFromTeacher:", e?.message || e);
        toast.error("Couldn’t remove this recording.");
      }
    },
    []
  );

  // Open modal for NEW theme
  const openCreateTheme = () => {
    setIsThemeModalOpen(true);
    setSelectedTheme("");
    setThemeSetting("");
    setThemeDescription("");
    setThemeComment("");
    setSelectedClasses(className ? [className] : []);
    setExpiryDate("");
    setEditingThemeRef(null);
    setEditingThemeDoc(null);
  };

  // Open modal for EDIT theme (prefill from doc)
  const openEditTheme = (t) => {
    setIsThemeModalOpen(true);
    setSelectedTheme(t.theme || "");
    setThemeSetting(t.setting || "");
    setThemeDescription(t.description || "");
    setThemeComment(t.comment || "");
    const classesVal = Array.isArray(t.classes) && t.classes.length ? t.classes : (t.className ? [t.className] : []);
    setSelectedClasses(classesVal);
    const expISO =
      t.expiryDate?.seconds
        ? new Date(t.expiryDate.seconds * 1000).toISOString().slice(0, 10)
        : (typeof t.expiryDate === "string" ? new Date(t.expiryDate).toISOString().slice(0, 10) : "");
    setExpiryDate(expISO || "");
    setEditingThemeRef(t.ref || null);
    setEditingThemeDoc(t || null);
  };

  // Delete a theme
  const handleDeleteTheme = async (t) => {
    try {
      if (!t?.ref) throw new Error("Missing document reference");
      await deleteDoc(t.ref);
      toast.success("🗑️ Theme deleted.");
    } catch (e) {
      console.error("deleteTheme:", e);
      toast.error("Couldn't delete the theme.");
    }
  };

  // Save Theme: create via callable OR update via ref
  const handleSaveTheme = useCallback(async () => {
    try {
      if (!selectedTheme.trim()) {
        toast.error("Please enter a theme name.");
        return;
      }

      // EDIT: update the existing doc (must include schoolId/className to satisfy rules)
      if (editingThemeRef && editingThemeDoc) {
        const write = {
          theme: selectedTheme.trim(),
          setting: (themeSetting || "").trim() || null,
          description: (themeDescription || "").trim() || null,
          comment: (themeComment || "").trim() || null,
          classes:
            Array.isArray(selectedClasses) && selectedClasses.length
              ? selectedClasses
              : (editingThemeDoc.className ? [editingThemeDoc.className] : []),
          className: editingThemeDoc.className || className || null,
          schoolId,                        // required by rules on update
          updatedAt: serverTimestamp(),
          expiryDate: expiryDate ? new Date(expiryDate).toISOString() : null,
        };
        await setDoc(editingThemeRef, write, { merge: true });
        toast.success("✏️ Theme updated.");
      } else {
        // CREATE: use callable (writes under classroom paths)
        const fns = getFunctions(app, "us-central1");
        const createClassroomTheme = httpsCallable(fns, "createClassroomTheme");

        const payload = {
          orgId: schoolId,
          theme: selectedTheme.trim(),
          setting: (themeSetting || "").trim() || null,
          description: (themeDescription || "").trim() || null,
          comment: (themeComment || "").trim() || null,
          classes:
            Array.isArray(selectedClasses) && selectedClasses.length
              ? selectedClasses
              : (className ? [className] : []),
          expiryDate: expiryDate || null,
        };

        await createClassroomTheme(payload);
        toast.success("✅ Theme saved to your classroom!");
      }

      // reset & close
      setIsThemeModalOpen(false);
      setEditingThemeRef(null);
      setEditingThemeDoc(null);
      setSelectedTheme("");
      setThemeSetting("");
      setThemeDescription("");
      setThemeComment("");
      setSelectedClasses([]);
      setExpiryDate("");
    } catch (e) {
      console.error("handleSaveTheme:", e);
      const msg = e?.message || e?.data?.message || "Failed to save theme";
      toast.error(
        msg.includes("Login required")
          ? "Please sign in again."
          : "You don't have permission to save themes. (Denied)"
      );
    }
  }, [
    selectedTheme,
    themeSetting,
    themeDescription,
    themeComment,
    selectedClasses,
    expiryDate,
    className,
    schoolId,
    editingThemeRef,
    editingThemeDoc,
  ]);


   const handleChangePassword = async () => {
  const user = auth.currentUser;
  if (!user) return toast.error("Please sign in again.");

  if (!pwNew || pwNew.length < 6) {
    return toast.error("New password must be at least 6 characters.");
  }
  if (pwNew !== pwConfirm) {
    return toast.error("New password and confirmation do not match.");
  }
  if (!pwCurrent) {
    return toast.error("Please enter your current password.");
  }

  setPwSaving(true);
  try {
    // Re-authenticate (required by Firebase for sensitive actions)
    const cred = EmailAuthProvider.credential(user.email, pwCurrent);
    await reauthenticateWithCredential(user, cred);

    await updatePassword(user, pwNew);

    toast.success("✅ Password updated.");
    setPwOpen(false);
    setPwCurrent("");
    setPwNew("");
    setPwConfirm("");
  } catch (e) {
    const code = e?.code || "";
    if (code.includes("wrong-password")) toast.error("Current password is incorrect.");
    else if (code.includes("too-many-requests")) toast.error("Too many attempts. Try again later.");
    else toast.error(e?.message || "Could not update password.");
  } finally {
    setPwSaving(false);
  }
};


const perDayMap = useMemo(() => {
  const selectedActivity = Array.isArray(selectedStudent?.readingActivity)
    ? selectedStudent.readingActivity
    : [];

  const m = {};
  selectedActivity.forEach((e) => {
    const key = e.date || e.ts || e.time || null;
    if (!key) return;
    const d = typeof key === "string" ? key : new Date(key).toISOString().slice(0, 10);
    const stories = Number(e.storiesGenerated || 0);
    const secs = Number(e.timeSpent || 0);
    m[d] = m[d] || { stories: 0, secs: 0 };
    m[d].stories += stories;
    m[d].secs += secs;
  });
  return m;
}, [selectedStudent]);

  const perDayLabels = useMemo(() => Object.keys(perDayMap).sort(), [perDayMap]);

  const selStoriesData = useMemo(
    () => ({
      labels: perDayLabels,
      datasets: [{ label: "Stories (per day)", data: perDayLabels.map((d) => perDayMap[d].stories), backgroundColor: "rgba(54, 162, 235, 0.6)" }],
    }),
    [perDayLabels, perDayMap]
  );

  const selTimeData = useMemo(
    () => ({
      labels: perDayLabels,
      datasets: [{ label: "Reading Time (mins)", data: perDayLabels.map((d) => Math.round(perDayMap[d].secs / 60)), borderColor: "rgba(75, 192, 192, 1)", backgroundColor: "rgba(75, 192, 192, 0.2)" }],
    }),
    [perDayLabels, perDayMap]
  );

  const selGenres = useMemo(() => {
    const g = selectedStudent?.genresExplored || selectedStudent?.favoriteGenres || {};
    const labels = Object.keys(g);
    const values = Object.values(g);
    return {
      labels,
      datasets: [{ label: "Genres", data: values, backgroundColor: ["rgba(255, 99, 132, 0.6)", "rgba(54, 162, 235, 0.6)", "rgba(255, 206, 86, 0.6)", "rgba(75, 192, 192, 0.6)", "rgba(153, 102, 255, 0.6)"] }],
    };
  }, [selectedStudent]);

  /* ----------------------------- loading gate -------------------------- */
  if (!uid || userLoading) {
    return (
      <div className="page-container" style={{ padding: 40 }}>
        <h2>Loading your account…</h2>
      </div>
    );
  }

  /* ----------------------------- chart options ------------------------- */

// If we have lots of students (or on mobile), switch to horizontal + scroll
const manyStudents = displayedStudents.length > 12 || isMobile;

// The visible "window" inside each card (scroll happens here)
const perStudentViewport = {
  maxHeight: 260,                 // 👈 fixed viewport height
  overflowY: manyStudents ? "auto" : "hidden",
  overflowX: "hidden",
  width: "100%",
};

// The actual canvas height (bigger list => taller canvas)
const perStudentCanvasHeight = manyStudents
  ? Math.min(1200, Math.max(360, displayedStudents.length * 28))
  : 200;

  const chartTitle = { marginBottom: "10px", fontSize: "14px" };
  // Hover insights: clearer tooltips
  const baseOptions = (xLabel) => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: manyStudents ? "y" : "x", // ✅ flip to horizontal when many
  layout: { padding: { bottom: 10 } },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      callbacks: {
        title: (items) => items?.[0]?.label ?? "",
        label: (ctx) => {
          const v = ctx.parsed?.x ?? ctx.parsed?.y ?? ctx.parsed ?? 0; // ✅ works for both orientations
          return `${ctx.dataset.label}: ${v}`;
        },
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      ticks: { autoSkip: true, maxTicksLimit: 8 },
      title: { display: true, text: xLabel },
    },
    y: {
  ticks: {
    autoSkip: false,
    maxTicksLimit: manyStudents ? 200 : 12,
    font: { size: 12 },
  },
},

  },
});


  /* -------------------------------- render ----------------------------- */
  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        minHeight: "100vh",
        fontFamily: "Montserrat, sans-serif",
        display: "flex",
        flexDirection: "column",
        overflowX: "auto",
      }}
    >
      {/* Header */}
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: "15px", justifyContent: "flex-end" }}>
          <button onClick={() => navigate("/help")} className="header-button">Help</button>
          <button onClick={() => navigate("/")} className="header-button">Home</button>
          <button
            onClick={handleLogout}
            className="header-button"
            style={{ backgroundColor: "blue", color: "white", fontWeight: "bold" }}
          >
            Logout
          </button>
        </nav>
      </header>

      {/* Body */}
      <div
        style={{
          display: "flex",
          padding: "20px",
          gap: "20px",
          overflowX: "auto",
          width: "100%",
          alignItems: "flex-start",
          ...(isMobile ? { minWidth: "900px" } : {}),
        }}
      >
        {/* Sidebar */}
        <aside
          style={{
            width: "250px",
            minWidth: "250px",
            flexShrink: 0,
            background: "rgba(230, 245, 255, 0.95)",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            position: "sticky",
            top: "20px",
            fontSize: "14px",
            lineHeight: "1.6",
          }}
        >
          <h2 style={{ color: "#007BFF", marginBottom: "10px" }}>🏫 {schoolName}</h2>
          <p style={{ marginBottom: "5px" }}>
            👩‍🏫 <strong>Teacher:</strong> {teacherDoc?.firstName} {teacherDoc?.lastName}
          </p>
          <p style={{ marginBottom: "10px", fontStyle: "italic" }}>
            📅 {new Date().toLocaleDateString()}
          </p>
          <p>🏷️ <strong>Class:</strong> {className || "—"}</p>
          <p>👥 <strong>Students:</strong> {students.length} / 30</p>
          <div style={{ background: "#ddd", borderRadius: "5px", height: "10px", overflow: "hidden", marginBottom: "10px" }}>
            <div
              style={{
                width: `${Math.min(100, (students.length / 30) * 100)}%`,
                background: "#007BFF",
                height: "100%",
              }}
            />
          </div>

          <button
  onClick={() => setPwOpen(true)}
  className="subscription-button"
  style={{ marginTop: 10, width: "100%" }}
>
  🔒 Change Password
</button>


          {/* NEW: Billing / Pool Period */}
          <div
            style={{
              marginTop: 8,
              background: "#f6f7fb",
              padding: 12,
              borderRadius: 8,
              border: "1px solid #e5e7eb",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>🗓️ Story Pool Period</div>
            <div style={{ fontSize: 13, color: "#334155" }}>
              <strong>Current period:</strong><br />
              {periodStart && periodEndDisplay
                ? `${formatDate(periodStart)} → ${formatDate(periodEndDisplay)}`
                : "N/A"}
            </div>
            <div style={{ fontSize: 13, color: "#334155", marginTop: 6 }}>
              <strong>Renews on:</strong> {renewDate ? formatDate(renewDate) : "N/A"}
            </div>
            {!schoolCtx?.billingAnchorMs && (
              <div style={{ fontSize: 12, color: "#9a3412", marginTop: 6 }}>
                This is linked to the school subscription period.
              </div>
            )}
          </div>

          {/* Mini leaderboard */}
          <div style={{ marginTop: 10, background: "#fff", borderRadius: 8, border: "1px solid #eee", padding: 10 }}>
            <h4 style={{ margin: "0 0 8px 0" }}>🏆 Class Leaderboard</h4>
            {leaderboard.length === 0 ? (
              <p style={{ color: "#777", margin: 0 }}>No data yet.</p>
            ) : (
              <table style={{ width: "100%", fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#555" }}>
                    <th>#</th>
                    <th>Student</th>
                    <th>Stories</th>
                    <th>Avg min</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((r) => (
                    <tr key={r.id}>
                      <td>{r.rank}</td>
                      <td>{r.name}</td>
                      <td>{r.stories}</td>
                      <td>{r.avgMin}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Recent submissions */}
          <div style={{ marginTop: 10, background: "#fff", borderRadius: 8, border: "1px solid #eee", padding: 10 }}>
            <h4 style={{ margin: "0 0 8px 0" }}>🆕 Recent Recording Submissions</h4>
            {recentSubmissions.length === 0 ? (
              <p style={{ color: "#777", margin: 0 }}>None yet.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {recentSubmissions.map((it, idx) => (
                  <li key={idx} style={{ marginBottom: 6 }}>
                    <span style={{ fontWeight: 600 }}>{it.who}</span> — {it.what}
                    <span style={{ color: "#666" }}> ({timeAgo(it.when)})</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Main */}
        <div style={{ flex: 1 }}>
          <h1
            style={{
              color: "#007BFF",
              textAlign: "center",
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 700,
              marginTop: 0,
              marginBottom: 10,
            }}
          >
            Welcome, {teacherDoc?.firstName} {teacherDoc?.lastName}!
          </h1>

          {/* Create Story Card */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.92)",
              padding: "30px",
              borderRadius: "10px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <h2 style={{ marginBottom: "10px", color: "#007BFF" }}>
              Create a Story for Your Class
            </h2>
            <p style={{ marginTop: 0, color: "#555", fontSize: "15px" }}>
              Use this to model story creation live during your lesson.
            </p>

            <button
              onClick={handleGenerateStory}
              style={{
                padding: "15px 30px",
                backgroundColor: "#007BFF",
                color: "white",
                border: "none",
                borderRadius: "5px",
                fontSize: "1rem",
                cursor: "pointer",
                marginTop: "10px",
                fontWeight: "bold",
              }}
            >
              Create a Story
            </button>
          </div>

          {/* Summary */}
          <div
            style={{
              background: "linear-gradient(to right, #2C3E50, #4CA1AF)",
              padding: "20px",
              borderRadius: "10px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
              textAlign: "center",
              marginBottom: "20px",
              color: "white",
            }}
          >
            <h2>📊 Reading Summary</h2>
            <p style={{ fontSize: "14px", opacity: 0.9 }}>
              A quick overview of your class reading progress.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                marginTop: "10px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3 style={{ color: "#F9C74F", fontSize: "1.8rem" }}>📖 {totalStories}</h3>
                <p>Stories Generated</p>
              </div>
              <div>
                <h3 style={{ color: "#F94144", fontSize: "1.8rem" }}>⏳ {avgReadingTime} min</h3>
                <p>Avg. Reading Time</p>
              </div>
              <div>
                <h3 style={{ color: "#90BE6D", fontSize: "1.8rem" }}>
                  🌟 {topStudent?.firstName || "-"} {topStudent?.lastName || ""}
                </h3>
                <p>Top Student</p>
              </div>
              <div>
                <h3 style={{ color: "#F3722C", fontSize: "1.8rem" }}>{students.length}</h3>
                <p>Students in Class</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div
            style={{
              background: "linear-gradient(to right, #2C3E50, #4CA1AF)",
              padding: "10px",
              borderRadius: "10px",
              margin: "20px 0",
            }}
          >
            <Tabs
              value={tab}
              onChange={(e, val) => setTab(val)}
              centered
              sx={{
                "& .MuiTab-root": { fontWeight: "bold", fontSize: "16px", color: "white", textTransform: "none" },
                "& .Mui-selected": { color: "#FFD700 !important" },
              }}
            >
              <Tab label="👨‍🏫 Students Performance" />
              <Tab label="📋 Student Data" />
              <Tab label="🎯 Themes" />
              <Tab
                label={
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    🎤 Recordings
                    {unreadCount > 0 && (
                      <span
                        style={{
                          background: "#FF4D4D",
                          color: "#fff",
                          borderRadius: 12,
                          padding: "2px 8px",
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {unreadCount}
                      </span>
                    )}
                  </span>
                }
              />
            </Tabs>
          </div>

          {/* Quick class filter + Export CSV */}
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
            {classOptions.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="class-filter-label">Filter by Class</InputLabel>
                <Select
                  labelId="class-filter-label"
                  label="Filter by Class"
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {classOptions.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <button
              onClick={() => {
                try {
                  const rows = displayedStudents.map((s) => {
                    const fullName = `${s.firstName || ""} ${s.lastName || ""}`.trim();
                    const stories = Number(s.storiesRead) || 0;
                    const totalMin = Math.round((Number(s.totalReadingTime) || 0) / 60);
                    const avgMin = stories ? Math.round(totalMin / stories) : 0;
                    const classLabel = s.className || "";
                    return { fullName, classLabel, stories, totalMin, avgMin };
                  });
                  const header = ["Student", "Class", "StoriesRead", "TotalMinutes", "AvgMinutesPerStory"];
                  const csv =
                    [header.join(","), ...rows.map(r => [r.fullName, r.classLabel, r.stories, r.totalMin, r.avgMin].join(","))].join("\n");
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `class-summary${filterClass ? "-" + filterClass : ""}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (e) {
                  console.error("Export CSV failed:", e);
                  toast.error("Could not export CSV.");
                }
              }}
              style={{
                padding: "8px 12px",
                backgroundColor: "#0ea5e9",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              ⬇️ Export CSV
            </button>
          </div>

          {/* Tab 0: Charts */}
{tab === 0 && (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: displayedStudents.length > 10 ? "1fr" : "1fr 1fr",
      gap: "20px",
      marginBottom: "40px",
    }}
  >
    {/* Stories */}
    <div style={chartCardStyle}>
      <h4 style={chartTitle}>📚 Stories Read</h4>
      <p style={{ fontSize: "13px", color: "#555" }}>
        Number of stories generated per student.
      </p>
      <div style={perStudentViewport}>
        <div style={{ height: perStudentCanvasHeight }}>
          <Bar data={storiesData} options={baseOptions("Student")} />
        </div>
      </div>
    </div>

    {/* Reading time */}
    <div style={chartCardStyle}>
      <h4 style={chartTitle}>⏱️ Reading Time</h4>
      <p style={{ fontSize: "13px", color: "#555" }}>
        Total reading time spent by each student (minutes).
      </p>
      <div style={perStudentViewport}>
        <div style={{ height: perStudentCanvasHeight }}>
          <Bar data={readingTimeData} options={baseOptions("Student")} />
        </div>
      </div>
    </div>

    {/* Genres */}
    <div style={chartCardStyle}>
      <h4 style={chartTitle}>🎭 Genres Explored</h4>
      <p style={{ fontSize: "13px", color: "#555" }}>
        Variety of genres students have explored.
      </p>
      <div style={{ height: 220 }}>
        <Bar data={genresData} options={baseOptions("Genre")} />
      </div>
    </div>

    {/* Engagement */}
    <div style={chartCardStyle}>
      <h4 style={chartTitle}>📈 Engagement</h4>
      <p style={{ fontSize: "13px", color: "#555" }}>
        Total days with any reading activity (all time).
      </p>
      <div style={perStudentViewport}>
        <div style={{ height: perStudentCanvasHeight }}>
          <Bar data={engagementData} options={baseOptions("Student")} />
        </div>
      </div>
    </div>
  </div>
)}


          {/* Tab 1: Table */}
          {tab === 1 && (
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "10px",
                padding: "20px",
                marginBottom: "30px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div style={{ paddingTop: "20px" }}>
                <h3>📋 View Student Data</h3>
                <input
                  type="text"
                  placeholder="Search by student name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: "10px",
                    borderRadius: "5px",
                    width: "100%",
                    marginBottom: "15px",
                    border: "1px solid #ccc",
                  }}
                />
                {displayedStudents.length === 0 ? (
                  <p style={{ color: "#777" }}>No students match your filters.</p>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: "600px",
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: "#f0f8ff" }}>
                          <th>Name</th>
                          <th>Class</th>
                          <th>Stories</th>
                          <th>Time (min)</th>
                          <th>Avg. Time</th>
                          <th>Genres</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedStudents.map((student, idx) => {
                          const totalMin = Math.round((Number(student.totalReadingTime) || 0) / 60);
                          const stories = Number(student.storiesRead) || 0;
                          const avgMin = stories ? Math.round(totalMin / stories) : 0;
                          return (
                            <tr key={idx} style={{ borderBottom: "1px solid #ccc" }}>
                              <td>{student.firstName} {student.lastName}</td>
                              <td>{student.className || "-"}</td>
                              <td>{stories}</td>
                              <td>{totalMin}</td>
                              <td>{avgMin}</td>
                              <td>
                                {Object.entries(
                                  student.genresExplored || student.favoriteGenres || {}
                                )
                                  .map(([genre, count]) => `${genre} (${count})`)
                                  .join(", ") || "None"}
                              </td>
                              <td>
                                <button
                                  onClick={() => handleOpenModal(student)}
                                  style={{
                                    padding: "6px 12px",
                                    backgroundColor: "#007BFF",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  View Insights
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab 2: Themes (with Edit/Delete) */}
          {tab === 2 && (
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                marginBottom: "30px",
              }}
            >
              <div style={{ paddingTop: "20px" }}>
                <h3>Custom Themes</h3>
                <button
                  onClick={openCreateTheme}
                  style={{
                    marginBottom: "20px",
                    padding: "10px 20px",
                    backgroundColor: "#28a745",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  ➕ Assign New Theme to Class
                </button>

                <h2 style={{ color: "#007BFF" }}>🎓 Active Assigned Themes</h2>

                {themes.length === 0 ? (
                  <p style={{ color: "#777" }}>No active themes have been assigned.</p>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                      gap: "20px",
                      marginTop: "20px",
                    }}
                  >
                    {themes.map((theme) => (
                      <div
                        key={`${theme.__path || ""}#${theme.id}`}
                        style={{
                          backgroundColor: "#ffffff",
                          padding: "20px",
                          borderRadius: "10px",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.08)",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <h3 style={{ marginBottom: "8px", color: "#007BFF", fontWeight: "600" }}>
                            {theme.theme}
                          </h3>
                          <p><strong>Setting:</strong> {theme.setting || "-"}</p>
                          <p><strong>Description:</strong> {theme.description || "-"}</p>
                          <p><strong>Message:</strong> {theme.comment || "-"}</p>
                          <p>
                            <strong>Classes:</strong>{" "}
                            {Array.isArray(theme.classes) && theme.classes.length
                              ? theme.classes.join(", ")
                              : theme.className || "-"}
                          </p>
                          <p>
                            <strong>Created:</strong>{" "}
                            {theme?.createdAt
                              ? formatDate(new Date(msFromAny(theme.createdAt)))
                              : "-"}
                          </p>
                          <p>
                            <strong>Expires:</strong>{" "}
                            {theme?.expiryDate
                              ? formatDate(new Date(msFromAny(theme.expiryDate)))
                              : "-"}
                          </p>
                        </div>
                        <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end", gap: 12 }}>
                          <button
                            onClick={() => openEditTheme(theme)}
                            style={{
                              background: "none",
                              color: "#007BFF",
                              border: "none",
                              cursor: "pointer",
                              fontWeight: "bold",
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTheme(theme)}
                            style={{
                              background: "none",
                              color: "#FF4D4D",
                              border: "none",
                              cursor: "pointer",
                              fontWeight: "bold",
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab 3: Recordings */}
          {tab === 3 && (
            <div style={{ background: "white", borderRadius: "10px", padding: "20px" }}>
              <h3 style={{ marginBottom: "10px" }}>🎤 Submitted Recordings</h3>
              <p style={{ fontSize: "14px", color: "gray", marginBottom: "20px" }}>
                These are recordings sent by your students.
              </p>

              {recordings.length === 0 ? (
                <p style={{ color: "#888" }}>No recordings have been submitted by students yet.</p>
              ) : (
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {recordings.map((r, i) => (
                    <li
                      key={`${r.fileName || r.id || i}`}
                      style={{
                        marginBottom: "15px",
                        padding: "15px",
                        borderRadius: "10px",
                        backgroundColor: r.viewedByTeacher ? "#f9f9f9" : "#e8f4ff",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <strong>{r.studentName}</strong> — {r.storyTitle || r.fileName}
                          {!r.viewedByTeacher && (
                            <span style={{ marginLeft: "10px", color: "#FF4D4D" }}>
                              🔔 New Recording
                            </span>
                          )}
                          <div style={{ fontSize: "0.85rem", color: "#555", marginTop: "4px" }}>
                            📅 {r.uploadedAt ? new Date(msFromAny(r.uploadedAt)).toLocaleString() : "Date unknown"}
                          </div>
                        </div>

                        <audio
                          controls
                          src={r.url}
                          style={{ minWidth: 280, flex: "0 1 auto" }}
                          onPlay={() => { if (!r.viewedByTeacher) markRecViewed(r); }}
                          onEnded={() => { if (!r.viewedByTeacher) markRecViewed(r); }}
                        />

                        <button
                          onClick={() => deleteRecordingFromTeacher(r)}
                          style={{
                            marginLeft: 8,
                            padding: "6px 10px",
                            backgroundColor: "#ff4d4d",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Student Insights Modal — SCROLLABLE with sticky header/footer */}
          {insightModalOpen && selectedStudent && (
            <div
              onClick={() => setInsightModalOpen(false)} // click outside closes
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
                padding: 20,
                overscrollBehavior: "contain",
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
                style={{
                  backgroundColor: "#fff",
                  borderRadius: 12,
                  width: "min(900px, 95vw)",
                  maxHeight: "90vh",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                  fontFamily: "Montserrat, sans-serif",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Sticky header */}
                <div
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    background: "#fff",
                    borderBottom: "1px solid #eee",
                    padding: "16px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div>
                    <h2 style={{ color: "#007BFF", margin: 0 }}>
                      📘 {selectedStudent.firstName} {selectedStudent.lastName}
                    </h2>
                    <p style={{ margin: 0, marginTop: 4, color: "#555" }}>
                      <strong>Class:</strong> {selectedStudent.className || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() => setInsightModalOpen(false)}
                    aria-label="Close"
                    title="Close"
                    style={{
                      border: "none",
                      background: "transparent",
                      fontSize: 22,
                      lineHeight: 1,
                      cursor: "pointer",
                      padding: 8,
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Scrollable body */}
                <div
                  style={{
                    padding: 20,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: 16,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #eee",
                        borderRadius: 10,
                        padding: 12,
                        height: 280,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <h4 style={{ margin: 0, marginBottom: 8 }}>Daily Stories</h4>
                      <p style={{ margin: 0, marginBottom: 8, fontSize: 12, color: "#666" }}>
                        Number of stories generated per day.
                      </p>
                      <div style={{ flex: 1 }}>
                        <Bar
                          data={selStoriesData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { display: false } },
                            scales: {
                              x: { title: { display: true, text: "Date" } },
                              y: { beginAtZero: true },
                            },
                          }}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #eee",
                        borderRadius: 10,
                        padding: 12,
                        height: 280,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <h4 style={{ margin: 0, marginBottom: 8 }}>Daily Time (mins)</h4>
                      <p style={{ margin: 0, marginBottom: 8, fontSize: 12, color: "#666" }}>
                        Total minutes read per day (sum of sessions for that date).
                      </p>
                      <div style={{ flex: 1 }}>
                        <Line
                          data={selTimeData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { display: false } },
                            scales: {
                              x: { title: { display: true, text: "Date" } },
                              y: { beginAtZero: true },
                            },
                          }}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #eee",
                        borderRadius: 10,
                        padding: 12,
                        height: 320,
                        gridColumn: isMobile ? "auto" : "1 / span 2",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <h4 style={{ margin: 0, marginBottom: 8 }}>Genres</h4>
                      <div style={{ flex: 1 }}>
                        <Pie
                          data={selGenres}
                          options={{ responsive: true, maintainAspectRatio: false }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sticky footer */}
                <div
                  style={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 2,
                    background: "#fff",
                    borderTop: "1px solid #eee",
                    padding: 12,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }}
                >
                  <button
                    onClick={() => setInsightModalOpen(false)}
                    style={{
                      backgroundColor: "#FF4D4D",
                      color: "#fff",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Theme Modal (Create & Edit) */}
          <Modal
            open={isThemeModalOpen}
            onClose={() => {
              setIsThemeModalOpen(false);
              setEditingThemeRef(null);
              setEditingThemeDoc(null);
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "white",
                padding: "30px",
                borderRadius: "10px",
                width: "90%",
                maxWidth: "600px",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: 24,
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              <h2 style={{ color: "#007BFF", marginBottom: "20px" }}>
                {editingThemeRef ? "✏️ Edit Theme" : "🎯 Assign a Custom Theme"}
              </h2>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontWeight: "bold" }}>Theme Name</label>
                <input
                  type="text"
                  value={selectedTheme}
                  onChange={(e) => setSelectedTheme(e.target.value)}
                  placeholder="e.g., Space Explorers"
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontWeight: "bold" }}>Setting (optional)</label>
                <input
                  type="text"
                  value={themeSetting}
                  onChange={(e) => setThemeSetting(e.target.value)}
                  placeholder="e.g., A forest, space station"
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontWeight: "bold" }}>Description (optional)</label>
                <textarea
                  value={themeDescription}
                  onChange={(e) => setThemeDescription(e.target.value)}
                  placeholder="Add extra details to inspire creativity..."
                  rows="3"
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontWeight: "bold" }}>Message to Students (optional)</label>
                <textarea
                  value={themeComment}
                  onChange={(e) => setThemeComment(e.target.value)}
                  placeholder="e.g., Try your best and have fun with this week’s theme!"
                  rows="2"
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontWeight: "bold" }}>
                  Assign to Class(es){editingThemeRef ? " (locked to existing classes)" : ""}
                </label>
                <select
                  multiple
                  value={selectedClasses}
                  onChange={(e) =>
                    setSelectedClasses(Array.from(e.target.selectedOptions, (o) => o.value))
                  }
                  disabled={!!editingThemeRef}
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
                >
                  {classOptions.map((cls) => (
                    <option key={cls} value={cls}>
                      {cls}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: "30px" }}>
                <label style={{ fontWeight: "bold" }}>Expiry Date</label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                {editingThemeRef && (
                  <button
                    onClick={async () => {
                      try {
                        await deleteDoc(editingThemeRef);
                        toast.success("🗑️ Theme deleted.");
                        setIsThemeModalOpen(false);
                        setEditingThemeRef(null);
                        setEditingThemeDoc(null);
                      } catch (e) {
                        console.error("deleteTheme (modal):", e);
                        toast.error("Couldn't delete the theme.");
                      }
                    }}
                    style={{ backgroundColor: "#ef4444", color: "#fff", border: "none", padding: "10px 14px", borderRadius: 6, fontWeight: 600 }}
                  >
                    Delete
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsThemeModalOpen(false);
                    setEditingThemeRef(null);
                    setEditingThemeDoc(null);
                  }}
                  style={{ backgroundColor: "#6b7280", color: "white", padding: "10px 14px", borderRadius: 6, border: "none" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTheme}
                  style={{ backgroundColor: "#007BFF", color: "white", padding: "10px 14px", borderRadius: 6, border: "none", marginLeft: "auto" }}
                >
                  {editingThemeRef ? "Save Changes" : "✅ Save Theme"}
                </button>
              </div>
            </Box>
          </Modal>

          <Modal
  open={pwOpen}
  onClose={() => {
    setPwOpen(false);
    setPwCurrent("");
    setPwNew("");
    setPwConfirm("");
  }}
>

  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "white",
      padding: "24px",
      borderRadius: "10px",
      width: "90%",
      maxWidth: "420px",
      boxShadow: 24,
      fontFamily: "Montserrat, sans-serif",
    }}
  >
    <h2 style={{ marginTop: 0, color: "#007BFF" }}>Change Password</h2>

    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <input
        type="password"
        placeholder="Current password"
        value={pwCurrent}
        onChange={(e) => setPwCurrent(e.target.value)}
        style={{ padding: 10, borderRadius: 6, border: "1px solid #ccc" }}
      />
      <input
        type="password"
        placeholder="New password (min 6 chars)"
        value={pwNew}
        onChange={(e) => setPwNew(e.target.value)}
        style={{ padding: 10, borderRadius: 6, border: "1px solid #ccc" }}
      />
      <input
        type="password"
        placeholder="Confirm new password"
        value={pwConfirm}
        onChange={(e) => setPwConfirm(e.target.value)}
        style={{ padding: 10, borderRadius: 6, border: "1px solid #ccc" }}
      />

      <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
        <button
          onClick={() => setPwOpen(false)}
          style={{
            backgroundColor: "#6b7280",
            color: "white",
            padding: "10px 14px",
            borderRadius: 6,
            border: "none",
            flex: 1,
            cursor: "pointer",
            fontWeight: 600,
          }}
          disabled={pwSaving}
        >
          Cancel
        </button>

        <button
          onClick={handleChangePassword}
          style={{
            backgroundColor: "#007BFF",
            color: "white",
            padding: "10px 14px",
            borderRadius: 6,
            border: "none",
            flex: 1,
            cursor: pwSaving ? "wait" : "pointer",
            fontWeight: 700,
            opacity: pwSaving ? 0.8 : 1,
          }}
          disabled={pwSaving}
        >
          {pwSaving ? "Saving…" : "Save"}
        </button>
      </div>

      <p style={{ fontSize: 12, color: "#64748b", margin: "10px 0 0" }}>
        If you’ve forgotten your current password, use “Forgot password” on the login page.
      </p>
    </div>
  </Box>
</Modal>


          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </div>
 </div>

  );
};

export default TeacherDashboard;
